import { Button } from "@/shared/components";
import { getThemeClass, getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import { sortQuotes } from "@/shared/redux/reducers/commonSlice";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

/* ================= TYPES ================= */

export type SortType = "price-asc" | "price-desc" | "insurer-asc" | "insurer-desc" | "clear";

/* ================= CONSTANTS ================= */

const sortLabelMap: Record<Exclude<SortType, "clear">, string> = {
  "price-asc": "Price: Low → High",
  "price-desc": "Price: High → Low",
  "insurer-asc": "Insurer: A → Z",
  "insurer-desc": "Insurer: Z → A",
};

/* ================= COMPONENT ================= */

const QuotesHeader = ({
  compact = false,
  totalQuotes,
  tabs = ["All"],
  activeTab = "All",
  onTabChange,
  quoteCountByTab,
  showDocumentUploadButton = false,
  onDocumentUpload,
}: {
  compact?: boolean;
  totalQuotes?: number;
  tabs?: string[];
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  quoteCountByTab?: Record<string, number>;
  showDocumentUploadButton?: boolean;
  onDocumentUpload?: () => void;
}) => {
  const cardClasses = getThemeComponentGroup("card");
  const inputClasses = getThemeComponentGroup("input");
  const pageShellClasses = getThemeComponentGroup("pageShell");
  const dispatch = useDispatch();

  const [openSort, setOpenSort] = useState(false);
  const [sortType, setSortType] = useState<SortType>("clear");

  const sortRef = useRef<HTMLDivElement>(null);

  const {
    commonSlice: { quoteList },
  } = usePosterReducers();

  const resolvedTotalQuotes =
    typeof totalQuotes === "number"
      ? totalQuotes
      : quoteList?.filter((quote) => {
          if ("error" in quote) return false;
          if (quote?.status) return false;
          if (!quote?.insurer?.name || !quote?.plan?.name) return false;

          const amount = Number(quote?.plan?.price?.amount);
          return Number.isFinite(amount) && amount > 0;
        }).length;

  /* ================= EFFECTS ================= */

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setOpenSort(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* ================= HANDLERS ================= */

  const handleSortChange = (type: SortType) => {
    setSortType(type);
    dispatch(sortQuotes(type));
  };

  const sortButtonLabel = sortType === "clear" ? "None" : sortLabelMap[sortType];

  /* ================= RENDER ================= */

  return (
    <div
      className={`rounded-lg ${
        compact ? "p-2" : "rounded-xl p-4"
      } ${getThemeClass(cardClasses.bgColor, "bg-theme-surface")} ${cardClasses.base}`}
    >
      <div
        className={`flex flex-col sm:flex-row sm:items-start sm:justify-between ${
          compact ? "gap-2" : "gap-3"
        }`}
      >
        {/* Tabs + Count */}
        <div
          className={`flex items-center font-semibold ${
            compact ? "gap-3 text-xs" : "gap-6 text-sm"
          }`}
        >
          {compact ? (
            <h2 className="flex items-center gap-1 whitespace-nowrap text-sm font-bold">
              {resolvedTotalQuotes} Quotes Found
              <span
                className={`text-xs font-normal ${getThemeClass(
                  pageShellClasses.mutedTextColor,
                  "text-theme-text-soft"
                )}`}
              >
                (Price exclusive of GST)
              </span>
            </h2>
          ) : null}

          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => onTabChange?.(tab)}
              className={`relative pb-1 transition ${
                activeTab === tab
                  ? "text-theme-primary"
                  : `${getThemeClass(
                      pageShellClasses.mutedTextColor,
                      "text-theme-text-soft"
                    )} hover:text-theme-text-muted`
              }`}
            >
              {tab}
              {typeof quoteCountByTab?.[tab] === "number" ? ` (${quoteCountByTab[tab]})` : ""}

              {activeTab === tab ? (
                <span className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-theme-primary" />
              ) : null}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex flex-col items-end gap-2">
          {/* Sort */}
          <div ref={sortRef} className="relative">
            <button
              type="button"
              onClick={() => setOpenSort((previousValue) => !previousValue)}
              className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                openSort
                  ? "border-theme-info-bright bg-theme-info-soft text-theme-info-text-strong"
                  : `${getThemeClass(
                      inputClasses.borderColor,
                      "border-theme-border-strong"
                    )} ${getThemeClass(cardClasses.bgColor, "bg-theme-surface")} ${getThemeClass(
                      pageShellClasses.mutedTextColor,
                      "text-theme-text-muted"
                    )} hover:bg-theme-surface-muted`
              } focus:outline-none focus:ring-2 focus:ring-theme-info-soft-alt`}
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M6 12h12M10 18h4" />
              </svg>

              <span
                className={getThemeClass(pageShellClasses.mutedTextColor, "text-theme-text-soft")}
              >
                Sort by
              </span>

              <span
                className={`font-medium ${getThemeClass(
                  pageShellClasses.mutedTextColor,
                  "text-theme-text-muted"
                )}`}
              >
                {sortButtonLabel}
              </span>

              <svg
                className={`h-4 w-4 transition-transform ${openSort ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown */}
            {openSort ? (
              <div
                className={`absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-xl border shadow-lg ${getThemeClass(
                  inputClasses.borderColor,
                  "border-theme-border"
                )} ${getThemeClass(cardClasses.bgColor, "bg-theme-surface")}`}
              >
                <div
                  className={`px-4 py-2 text-xs font-semibold uppercase ${getThemeClass(
                    pageShellClasses.mutedTextColor,
                    "text-theme-text-soft"
                  )}`}
                >
                  Sort Options
                </div>

                <button
                  type="button"
                  onClick={() => {
                    handleSortChange("clear");
                    setOpenSort(false);
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-theme-danger-bright transition-colors hover:bg-theme-danger-soft"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear sort
                </button>

                <div
                  className={`my-1 border-t ${getThemeClass(
                    inputClasses.borderColor,
                    "border-theme-border"
                  )}`}
                />

                {Object.entries(sortLabelMap).map(([key, label]) => {
                  const isActive = sortType === key;

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        handleSortChange(key as SortType);
                        setOpenSort(false);
                      }}
                      className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm transition-colors ${
                        isActive
                          ? "bg-theme-info-soft font-medium text-theme-info-text-strong"
                          : `${getThemeClass(
                              pageShellClasses.mutedTextColor,
                              "text-theme-text-muted"
                            )} hover:bg-theme-surface-muted`
                      }`}
                    >
                      <span>{label}</span>

                      {isActive ? (
                        <svg
                          className="h-4 w-4 text-theme-info-bright"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          {showDocumentUploadButton ? (
            <Button type="button" onClick={onDocumentUpload}>
              Document Upload
            </Button>
          ) : null}
        </div>
      </div>

      {/* Bottom row */}
      {!compact ? (
        <div className="mt-3">
          <h2 className="flex items-center gap-2 text-lg font-bold">
            {resolvedTotalQuotes} Quotes Found
            <span
              className={`text-xs font-normal ${getThemeClass(
                pageShellClasses.mutedTextColor,
                "text-theme-text-soft"
              )}`}
            >
              (Price exclusive of GST)
            </span>
          </h2>
        </div>
      ) : null}
    </div>
  );
};

export default QuotesHeader;
